import { useQuery } from 'react-query';
import { getPatientNutrients } from '../api/getPatientNutrients';

/**
 * Custom hook to fetch and process food group data from workflow
 *
 * @param {string} workflowToken - The workflow token to fetch data for
 * @returns {Object} - Food groups data with loading state
 */
const useFoodGroups = (workflowToken) => {
  const { data: workflowData, isLoading } = useQuery(
    `workflow_food_groups_${workflowToken}`,
    () => getPatientNutrients(workflowToken),
    {
      enabled: !!workflowToken,
      refetchOnWindowFocus: false,
      staleTime: 3600000, // 1 hour
    },
  );

  // Extract and process food group data
  const getFoodGroupsData = () => {
    if (!workflowData?.diet_id?.food_group_info || !workflowData?.diet_ideal?.food_group_info) {
      return [];
    }

    const currentDiet = workflowData.diet_id.food_group_info;
    const idealDiet = workflowData.diet_ideal.food_group_info;

    // Get meta food groups and process them
    return (
      Object.keys(currentDiet)
        .filter((key) => key.startsWith('meta-'))
        .map((key) => {
          const current = currentDiet[key];
          const ideal = idealDiet[key] || { value: 0 };

          // Calculate change between ideal and current
          const currentValue = parseFloat(current.value.toFixed(1));
          const idealValue = parseFloat(ideal.value.toFixed(1));
          const change = parseFloat((idealValue - currentValue).toFixed(1));

          return {
            id: key,
            name: current.label || key.replace('meta-', '').replace(/-/g, ' '),
            currentValue,
            idealValue,
            change,
            suppressIncrease: current.suppress_increase || false,
            suppressDecrease: current.suppress_decrease || false,
          };
        })
        // Sort by absolute change value (largest changes first)
        .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
    );
  };

  const foodGroups = !isLoading && workflowData ? getFoodGroupsData() : [];

  return {
    data: foodGroups,
    isLoading,
    toIncrease: workflowData?.to_increase || [],
    toDecrease: workflowData?.to_decrease || [],
    originalData: workflowData,
  };
};

export default useFoodGroups;
